import React, { useState, useRef, useEffect } from 'react';
import Select from 'react-select';
import { AppBar, Toolbar, Typography, IconButton, Box } from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate, useLocation } from 'react-router-dom';
import logo from '../Navbar/logo.png';
import './Navbar.css';
import { useUser } from "../context/UserContext";
import { getPageTitle } from '../utils/getPageTitle';

const formatOption = (opt) => (
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  }}>
    <div>
      <div>{opt.label}</div>
      <div style={{ fontSize: '0.8em', color: '#666' }}>
        {opt.usertype}
      </div>
    </div>
    {opt.number && (
      <div style={{ fontSize: '0.8em', color: '#333' }}>
        {opt.number}
      </div>
    )}
  </div>
);

const Navbar = ({ onLogout }) => {
  const { selectedCenter, userData } = useUser();
  const location = useLocation();
  const pageTitle = getPageTitle(location.pathname);
  const navigate = useNavigate();
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const abortControllerRef = useRef(null);
  const [isSearching, setIsSearching] = useState(false);
  const userId = userData?.userId;
  const [selectedUser, setSelectedUser] = useState(null);
  const token = localStorage.getItem('auth-token');

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const handleInputChange = (newValue) => {
    setInputValue(newValue);

    if (!newValue.trim() || newValue.trim().length < 3) {
      setOptions([]);
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;
    setIsSearching(true);

    fetch(
      `${process.env.REACT_APP_API_URL_API_SEARCH_USER}?query=${encodeURIComponent(newValue)}&userId=${userId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
        },
        signal: controller.signal
      }
    )
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(json => {
        if (controller.signal.aborted) return;
        
        const players = Array.isArray(json.data?.data) ? json.data.data : [];
        const academies = Array.isArray(json.data?.data2) ? json.data.data2 : [];

        const playerOpts = players.map(u => ({
          value: u.id,
          label: u.name || '',
          usertype: u.user_type,
          number: u.mobile_number || '',
          tag: 'PLAYER',
        }));

        const academyOpts = academies.map(a => ({
          value: a.id,
          label: a.name,
          usertype: 'ACADEMY',
          number: '',
          tag: 'ACADEMY',
        }));

        const finalOptions = userData?.userType === 'ADMIN'
          ? [...playerOpts, ...academyOpts]
          : playerOpts;

        setOptions(finalOptions);
      })
      .catch(err => {
        if (err.name !== 'AbortError') {
          console.error('Search error', err);
          setOptions([]);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsSearching(false);
        }
      });
  };

  const customNoOptionsMessage = ({ inputValue }) => {
    if (isSearching) return 'Searching...';
    return inputValue ? 'No users found' : 'Type to search';
  };

  const handleChange = async (val) => {
    setSelectedUser(val);
    if (!val) {
      navigate('/');
      return;
    }

    if (val.tag === 'ACADEMY') {
      navigate('/newAcademy', {
        state: {
          academy: { id: val.value }
        }
      });
      return;
    }

    const type = (val.usertype || '').toLowerCase();
    const defaultRouteState = { state: { userId: val.value } };

    switch (type) {
      case 'admin':
      case 'super admin':
      case 'academy':
        navigate('/profile', defaultRouteState);
        break;
      case 'guest':
        navigate('/guestUser', defaultRouteState);
        break;
      case 'player':
        navigate('/playerDetails', defaultRouteState);
        break;
      case 'family':
        navigate('/family', defaultRouteState);
        break;
      case 'coach':
        try {
          const res = await fetch(`${process.env.REACT_APP_API_URL_API_SEARCH_COACH}/${val.value}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
          });
          if (!res.ok) throw new Error();
          const json = await res.json();
          const coachId = json.data.coach.id;
          navigate('/createCoach', { state: { coachId } });
        } catch (err) {
          console.error(err);
        }
        break;
      default:
        navigate('/playerDetails', defaultRouteState);
    }
  };

  useEffect(() => {
    setSelectedUser(null);
    setInputValue('');
    setOptions([]);
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.clear();
    onLogout();
    navigate('/login');
  };

  return (
    <AppBar id="navbar" position="static" elevation={2}>
      <Toolbar className="navbar-toolbar" sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box className="navbar-logo-container" onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
            <img src={logo} alt="Company Logo" className="navbar-logo" />
          </Box>

          <Box
            sx={{
              ml: '45%',
              minWidth: '250px',
              maxWidth: '250px',
              zIndex: 9999,
              display: { xs: 'none', sm: 'none', md: 'flex' }
            }}
          >
            <Select
              name="player"
              options={options}
              value={selectedUser}
              onInputChange={handleInputChange}
              onChange={handleChange}
              placeholder="Search"
              className={`walkin-form-dropdown ${selectedUser ? 'has-value' : ''}`}
              classNamePrefix="walkin-select"
              isClearable
              noOptionsMessage={customNoOptionsMessage}
              isLoading={isSearching}
              loadingMessage={() => 'Searching...'}
              formatOptionLabel={formatOption}
              formatSingleValue={formatOption}
              filterOption={null}
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  minHeight: '40px',
                  height: '40px',
                  padding: '0 4px',
                  border: isSearching ? '1px solid #2684FF' : provided.border,
                  boxShadow: state.isFocused ? '0 0 0 1px #2684FF' : provided.boxShadow,
                  '&:hover': {
                    border: isSearching ? '1px solid #2684FF' : provided.border
                  }
                }),
                valueContainer: provided => ({
                  ...provided,
                  height: '40px',
                  padding: '0 4px'
                }),
                indicatorsContainer: provided => ({
                  ...provided,
                  height: '40px'
                }),
                dropdownIndicator: provided => ({
                  ...provided,
                  padding: 0
                }),
                clearIndicator: provided => ({
                  ...provided,
                  padding: 0
                }),
                placeholder: provided => ({
                  ...provided,
                  margin: 0,
                  lineHeight: '40px',
                  color: '#000'
                }),
                input: provided => ({
                  ...provided,
                  margin: 0,
                  padding: 0,
                  lineHeight: '40px'
                }),
              }}
            />
          </Box>
        </Box>

        <Typography
          variant="h5"
          className="navbar-title"
          sx={{
            flexGrow: 1,
            textAlign: 'center',
            fontSize: 25,
            textTransform: 'uppercase',
          }}
        >
          {pageTitle}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            size="large"
            aria-label="profile"
            color="inherit"
            onClick={() => navigate('/profile')}
            style={{padding:'7px' }}
          >
            <AccountCircle />
          </IconButton>

          <IconButton
            size="large"
            aria-label="logout"
            color="inherit"
            onClick={handleLogout}
            style={{padding:'7px' }}
          >
            <LogoutIcon />
          </IconButton>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;